// Contract templates and the contract documents built from them.
//
// Pure by design: no Firebase, no React. Everything that used to be decided by the
// hardcoded `contracttype` string lives here instead.
//
// The point of the template is that `contracttype` was carrying four unrelated jobs at
// once: whether to create a contract at all, which terms text to use, which standard-part
// PDF, and whether validuntil gets set. Only the last is real behaviour, and it is now
// `template.validity`. `contracttype` survives as a free-text LABEL the admin types - it
// is shown and filtered on, and nothing branches on it.

export const LANGUAGES = [
  { value: 'fi', label: 'Suomi' },
  { value: 'sv', label: 'Svenska' },
  { value: 'en', label: 'English' },
]

export const VALIDITY_OPTIONS = [
  { value: 'fixedterm', label: 'Määräaikainen' },
  { value: 'openended', label: 'Toistaiseksi voimassa' },
]

export const SIGNING_OPTIONS = [
  { value: 'digital', label: 'Sähköinen' },
  { value: 'manual', label: 'Manuaalinen' },
]

export const SUBJECT_OPTIONS = [
  { value: 'reservation', label: 'Varaus' },
  { value: 'organisation', label: 'Organisaatio' },
  { value: 'both', label: 'Molemmat' },
]

export const TEMPLATE_STATES = [
  { value: 'active', label: 'Käytössä' },
  { value: 'paused', label: 'Ei käytössä' },
]

// The signing lifecycle. 'created' and 'cancelled' are written here; 'pendingsignature'
// and 'signed' are written by the backend trigger that dispatches the e-signature.
//
// Note this is `status`, which is NOT the same field as `state`. `state: 'deleted'` is the
// soft-delete marker every entity in this repo uses, and the two are deliberately kept
// apart: a deleted contract and a cancelled one are different things.
export const CONTRACT_STATUSES = [
  { value: 'created', label: 'Luotu' },
  { value: 'pendingsignature', label: 'Odottaa allekirjoitusta' },
  { value: 'signed', label: 'Allekirjoitettu' },
  { value: 'cancelled', label: 'Peruttu' },
]

const emptyLangs = () => ({ fi: '', sv: '', en: '' })

export const EMPTY_TEMPLATE = {
  name: '',
  description: '',
  contracttype: '',
  validity: 'fixedterm',
  signingtype: 'digital',
  standardpartfile: '',
  subjecttype: 'reservation',
  venues: [],
  legacytype: '',
  order: 0,
  state: 'active',
}

export const emptyTemplate = () => ({
  ...EMPTY_TEMPLATE,
  venues: [],
  terms: emptyLangs(),
  coveringnote: emptyLangs(),
  company: { name: '', streetaddress: '', businessid: '', email: '', phone: '' },
})

export const labelFor = (options, value) => {
  const found = options.find((o) => o.value === value)
  return found ? found.label : value || ''
}

/* Picking a template */

// The templates offered for a given subject at a given venue. An empty venues[] means the
// template is offered everywhere, which is the common case.
export const templatesFor = (templates, subjecttype, venue) =>
  (templates || [])
    .filter((t) => t.state !== 'deleted' && t.state !== 'paused')
    .filter((t) => !subjecttype || t.subjecttype === 'both' || t.subjecttype === subjecttype)
    .filter((t) => !venue || !t.venues || t.venues.length === 0 || t.venues.includes(venue))
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0) || String(a.name || '').localeCompare(String(b.name || ''), 'fi'))

/* The signing party */

// Who signs, and at which address. An organisation payer carries its identity on
// contacts[0]; a person payer carries it directly. This mirrors the shape
// Reservation.getFirstPayerSimple()/getPayerOrganisation() expect, but it must not throw
// on a malformed payer - a booking with no contacts is a real thing in this data.
export const payerIdentity = (reservation) => {
  const payer =
    reservation && reservation.persons && reservation.persons.payers && reservation.persons.payers[0]
      ? reservation.persons.payers[0]
      : null
  if (!payer) return null

  const contact = payer.contacts && payer.contacts[0] ? payer.contacts[0] : null
  const isOrganisation = !!payer.businessId

  const name = payer.name
    ? payer.name
    : [payer.firstname, payer.lastname].filter(Boolean).join(' ').trim()

  // The email check the modal already does before offering a contract at all.
  const email = payer.email || (contact ? contact.email : '') || ''

  // The customer's id, NOT the logged-in admin's - which is what the old code stored.
  const id = isOrganisation ? (contact ? contact.personid || contact.id || '' : '') : payer.id || ''

  return {
    id,
    name: name || email,
    email,
    organisationid: isOrganisation ? payer.id || '' : '',
  }
}

// A contract can only be signed by someone with an address to send it to.
export const hasSignableEmail = (reservation) => {
  const identity = payerIdentity(reservation)
  return !!(identity && identity.email)
}

/* Building the contract */

// The contract document, from a template plus whatever it is about.
//
// Replaces the two verbatim-duplicated blocks that used to sit in ReservationModal's
// update and create save paths.
//
// `type` is still written from template.legacytype: the PDF and the e-signature dispatch
// run in a Firestore trigger in another repo, which reads `type` today. Writing both lets
// that backend move to `templateid` on its own schedule instead of needing a flag day.
export const buildContractFromTemplate = (template, context) => {
  if (!template) return null
  const ctx = context || {}
  const { reservation, organisation } = ctx
  const identity = ctx.payer || (reservation ? payerIdentity(reservation) : null)

  const contract = {
    templateid: template.id || '',
    templatename: template.name || '',
    // Label, copied so the contracts list can show and filter it without a join.
    contracttype: template.contracttype || '',
    displayName: template.name || 'Sopimus',
    signingtype: ctx.signingtype || template.signingtype || 'digital',
    status: 'created',
    subjecttype: organisation ? 'organisation' : 'reservation',
    user: identity ? identity.name : '',
    userid: identity ? identity.id : '',
    useremail: identity ? identity.email : '',
  }

  if (template.legacytype) contract.type = template.legacytype

  if (organisation) {
    contract.organisationid = organisation.id || ''
    if (!contract.user) contract.user = organisation.name || ''
  } else if (reservation) {
    contract.reservationid = reservation.id || ''
    if (identity && identity.organisationid) contract.organisationid = identity.organisationid
  }

  // The one behaviour contracttype used to hide. An open-ended contract has no end date,
  // so validuntil is left off entirely rather than written empty.
  if (template.validity === 'fixedterm') {
    const until = ctx.validuntil || (reservation ? reservation.recurrencyEndDate : '')
    if (until) contract.validuntil = until
  }

  return contract
}

/* Seeding from the old config */

// The standard part - the immutable boilerplate the authored terms are appended to. These
// two URLs were hardcoded in the terms editor, one per contract type, which is exactly
// what capped the venue at two contract types. On a template it is just a field.
export const STANDARD_PART_URLS = {
  fixedterm: 'https://cfalconassets.s3.eu-north-1.amazonaws.com/public/fixedterm.pdf',
  untilfurthernotice: 'https://cfalconassets.s3.eu-north-1.amazonaws.com/public/untilfurthernotice.pdf',
}

const LEGACY_TYPES = {
  fixedterm: { legacytype: 'standardshift-fixedterm', validity: 'fixedterm', label: 'Määräaikainen' },
  untilfurthernotice: {
    legacytype: 'standardshift-untilfurthernotice',
    validity: 'openended',
    label: 'Toistaiseksi voimassa',
  },
}

const hasText = (block) => {
  if (!block) return false
  const langs = ['fi', 'sv', 'en']
  const filled = (obj) => !!obj && langs.some((l) => obj[l] && String(obj[l]).trim().length > 0)
  return filled(block.text) || filled(block.coveringnote)
}

// Turns the old config/recurrentterms blob into template drafts, one per (venue, type)
// pair that actually has wording in it.
//
// Shape in: terms[venueName | 'default'][fixedterm | untilfurthernotice].{text,coveringnote}[fi|sv|en]
// plus terms[venueName].company{...}
//
// Note the old keys are 'fixedterm'/'untilfurthernotice' while the reservation wrote
// 'standardshift-fixedterm' - a mismatch only an undocumented server-side mapping could
// bridge. Seeding records both, and after this the mismatch has nowhere left to live.
export const templatesFromRecurrentTerms = (terms) => {
  if (!terms || typeof terms !== 'object') return []
  const drafts = []
  Object.keys(terms).forEach((venueKey) => {
    const venueBlock = terms[venueKey]
    if (!venueBlock || typeof venueBlock !== 'object') return
    Object.keys(LEGACY_TYPES).forEach((typeKey) => {
      const block = venueBlock[typeKey]
      if (!hasText(block)) return
      const meta = LEGACY_TYPES[typeKey]
      drafts.push({
        ...emptyTemplate(),
        name: venueKey === 'default' ? meta.label : meta.label + ' – ' + venueKey,
        contracttype: meta.label,
        validity: meta.validity,
        legacytype: meta.legacytype,
        standardpartfile: STANDARD_PART_URLS[typeKey] || '',
        terms: { ...emptyLangs(), ...(block.text || {}) },
        coveringnote: { ...emptyLangs(), ...(block.coveringnote || {}) },
        company: { ...emptyTemplate().company, ...(venueBlock.company || {}) },
        venues: venueKey === 'default' ? [] : [venueKey],
        subjecttype: 'reservation',
        order: drafts.length,
      })
    })
  })
  return drafts
}
